import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, X } from 'lucide-react';

import { useBackupState } from '@/modules/settings/useBackupState';
import { jetztSichern, useAutoSicherung } from '@/modules/settings/autoSicherungDienst';
import { cn } from '@/lib/utils/cn';
import { de } from '@/i18n/de';

/**
 * Erinnerung an die Sicherung.
 *
 * Ab sieben Tagen wegklickbar, ab 21 Tagen nicht mehr. Das ist absichtlich
 * leicht nervig: die Daten liegen in genau einem Browser, und ein Backup, das
 * nie heruntergeladen wird, ist keins.
 *
 * Wartet die automatische Sicherung (F-46) auf die Ordnerfreigabe, zeigt der
 * Balken statt des Links einen Knopf "Freigeben": Chrome erlaubt die Abfrage
 * nur nach einem Klick, und ohne sie laeuft die woechentliche Sicherung nicht.
 */
export function BackupNag() {
  const { shouldNag, isUrgent, daysSinceExport } = useBackupState();
  const freigabe = useAutoSicherung((s) => s.status === 'freigabe' && s.faellig);
  const [dismissed, setDismissed] = useState(false);

  if (!shouldNag && !freigabe) return null;
  if (dismissed && !isUrgent) return null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2.5 text-sm sm:px-6',
        isUrgent
          ? 'border-danger/30 bg-danger/10 text-ink'
          : 'border-line bg-surface-raised text-ink-muted',
      )}
    >
      <ShieldAlert className={cn('size-4 shrink-0', isUrgent && 'text-danger')} />
      <span className="min-w-0 flex-1">
        {freigabe
          ? de.backup.auto.bannerPermission
          : daysSinceExport === null ? de.backup.nagNever : de.backup.nagBody(daysSinceExport)}
      </span>
      {freigabe ? (
        <button
          type="button"
          onClick={() => void jetztSichern()}
          className="shrink-0 font-medium text-accent-text underline-offset-2 hover:underline"
        >
          {de.backup.auto.grant}
        </button>
      ) : (
        <Link
          to="/einstellungen#sicherung"
          className="shrink-0 font-medium text-accent-text underline-offset-2 hover:underline"
        >
          {de.backup.export}
        </Link>
      )}
      {!isUrgent && (
        <button
          type="button"
          aria-label={de.backup.nagDismiss}
          onClick={() => setDismissed(true)}
          className="grid size-6 shrink-0 place-items-center rounded text-ink-faint hover:text-ink"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}
